import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import Tooltip from '@atlaskit/tooltip';
import { SmartLinkInternalTheme, SmartLinkSize, SmartLinkTheme } from '../../../../../constants';
import { getLinkLineHeight, getLinkSizeStyles, getTruncateStyles, hasWhiteSpace } from '../../utils';
import { useMouseDownEvent } from '../../../../../state/analytics/useLinkClicked';
var DEFAULT_MAX_LINES = 2;
var MAXIMUM_MAX_LINES = 2;
var MINIMUM_MAX_LINES = 1;
var containerStyles = css({
  flex: '1 1 auto'
});
var getThemeStyles = function getThemeStyles(theme) {
  switch (theme) {
    case SmartLinkInternalTheme.Grey:
      // We are being specifc with the CSS selectors to ensure that Confluence overrides
      // do not affect our internal Smart Card styles
      return css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
        'a&': {
          color: "var(--ds-text-subtlest, #626F86)",
          '&:active, &:visited, &:focus, &:hover': {
            color: "var(--ds-text-subtlest, #626F86)",
            textDecoration: 'underline'
          },
          fontSize: '12px'
        }
      });
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    case SmartLinkTheme.Black:
      return css({
        color: "var(--ds-text-subtle, #44546F)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
        ':active': {
          color: "var(--ds-text, #172B4D)"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
        ':hover': {
          color: "var(--ds-text-subtle, #44546F)",
          textDecoration: 'underline'
        }
      });
    case SmartLinkTheme.Link:
    default:
      return css({
        color: "var(--ds-link, #0C66E4)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
        ':active': {
          color: "var(--ds-link-pressed, #0055CC)"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
        ':hover': {
          color: "var(--ds-link, #0C66E4)",
          textDecoration: 'underline'
        }
      });
  }
};
var getAnchorStyles = function getAnchorStyles(size, theme, maxLines, hasSpace) {
  var sizeStyles = getLinkSizeStyles(size);
  return css({
    flex: '1 1 auto'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  sizeStyles,
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  getTruncateStyles(maxLines, getLinkLineHeight(size), hasSpace ? 'break-word' : 'break-all'),
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  getThemeStyles(theme));
};
var getMaxLines = function getMaxLines(maxLines) {
  if (maxLines > MAXIMUM_MAX_LINES) {
    return DEFAULT_MAX_LINES;
  }
  if (maxLines < MINIMUM_MAX_LINES) {
    return MINIMUM_MAX_LINES;
  }
  return maxLines;
};
var withTooltip = function withTooltip(trigger, content, testId) {
  return jsx(Tooltip, {
    content: content,
    testId: "".concat(testId, "-tooltip"),
    tag: "span"
  }, trigger);
};

/**
 * A base element that represent an anchor.
 * @internal
 * @param {LinkProps} LinkProps - The props necessary for the Link element.
 * @see LinkIcon
 */
var Link = function Link(_ref) {
  var hideTooltip = _ref.hideTooltip,
    _ref$maxLines = _ref.maxLines,
    maxLines = _ref$maxLines === void 0 ? DEFAULT_MAX_LINES : _ref$maxLines,
    name = _ref.name,
    overrideCss = _ref.overrideCss,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? SmartLinkSize.Medium : _ref$size,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'smart-element-link' : _ref$testId,
    text = _ref.text,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? SmartLinkTheme.Link : _ref$theme,
    url = _ref.url,
    onClick = _ref.onClick,
    _ref$target = _ref.target,
    target = _ref$target === void 0 ? '_blank' : _ref$target;
  var onMouseDown = useMouseDownEvent();
  var hasSpace = useMemo(function () {
    return text ? hasWhiteSpace(text) : false;
  }, [text]);
  var anchor = jsx("a", _extends({
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    css: [getAnchorStyles(size, theme, getMaxLines(maxLines), hasSpace), overrideCss],
    "data-smart-element": name,
    "data-smart-element-link": true,
    "data-testid": testId,
    onClick: onClick,
    onMouseDown: onMouseDown,
    href: url
    // We do not want set the target if it is the default value of '_self'. This prevents link
    // click issues in Confluence and Trello which rely on it not being set unless necessary.
  }, target !== '_self' && {
    target: target
  }), text);
  return jsx("span", {
    css: containerStyles
  }, hideTooltip || text === undefined ? anchor : withTooltip(anchor, text, testId));
};
export default Link;